import { createContext, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"
import useScrollContext from "@/hooks/useScrollContext"
import { removeAllQueryParamsFromAsPath } from "@/utils/utils"
import useCustomContext from "@/hooks/useCustomContext"
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect"

export const PageTransitionContext = createContext({
	isTransitioning: false,
	ended: false,
})

export default function PageTransition({ children }) {
	const router = useRouter()
	const [isTransitioning] = useState(true)
	const [loaded, setLoaded] = useState(false)
	const prevPath = useRef("")

	const context = useMemo(
		() => ({
			isTransitioning,
		}),
		[isTransitioning],
	)

	//this props skips query params changes and will update only on route change
	const redirect = useMemo(() => removeAllQueryParamsFromAsPath(router.asPath), [router.asPath])
	const scroll = useScrollContext()
	const target = useRef(0)

	useEffect(() => {
		setLoaded(true)
		scroll.unlock()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Simulate loading by checking when components have fully loaded
	useEffect(() => {
		const handleRouteChange = () => {
			setLoaded(false)
		}

		const handleRouteComplete = () => {
			setLoaded(true)
		}

		// Set up Next.js route events to manage loading state
		router.events.on("routeChangeStart", handleRouteChange)
		router.events.on("routeChangeComplete", handleRouteComplete)

		return () => {
			router.events.off("routeChangeStart", handleRouteChange)
			router.events.off("routeChangeComplete", handleRouteComplete)
		}
	}, [router])

	useEffect(() => {
		if (!loaded) {
			scroll.lock()
		}

		return () => {
			if (!loaded) {
				scroll.unlock()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loaded])

	useIsomorphicLayoutEffect(() => {
		// logNamed({ loaded, redirect })
		if (!loaded || prevPath.current === redirect) return
		const hash = router.asPath.split("#")[1]
		//reset pos for correct calculations
		scroll.scrollTo({
			top: 0,
			left: 0,
			behavior: "instant",
		})
		if (hash) {
			const el = document.getElementById(hash)
			// console.log(el, hash);
			if (el) {
				const elTop = el.getBoundingClientRect().y
				target.current = Math.max(0, elTop)

				scroll.scrollTo({
					top: target.current,
					left: 0,
					behavior: "instant",
				})
				target.current = 0
			}
		}

		prevPath.current = redirect
	}, [loaded, redirect])

	return <PageTransitionContext.Provider value={context}>{children}</PageTransitionContext.Provider>
}

export function usePageTransitionContext(selector = undefined) {
	return useCustomContext(PageTransitionContext, selector)
}
